import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { 
  Calendar as CalendarIcon, Clock, MapPin, Video, 
  Users, Plus, ExternalLink, Check, Tent, Sun, Edit, Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = {
  family_fun_day: { label: 'Family Fun Day', color: 'bg-green-500' },
  webinar: { label: 'Webinar', color: 'bg-blue-500' },
  workshop: { label: 'Workshop', color: 'bg-purple-500' },
  meetup: { label: 'Meetup', color: 'bg-orange-500' },
  camp: { label: 'Inclusive Camp', color: 'bg-teal-500' }
};

export default function Events() {
  const { user, isAuthenticated, isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editCampOpen, setEditCampOpen] = useState(false);
  const [editingCamp, setEditingCamp] = useState(null);
  const [rsvpData, setRsvpData] = useState({
    adults: [{ name: '', age: '', support_needs: '', allergies: '' }],
    children: []
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchCamps();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCamps = async () => {
    try {
      const response = await axios.get(`${API}/camps`);
      setCamps(response.data);
    } catch (error) {
      console.error('Failed to fetch camps:', error);
    }
  };

  const addAttendee = (type) => {
    setRsvpData(prev => ({
      ...prev,
      [type]: [...prev[type], { name: '', age: '', support_needs: '', allergies: '' }]
    }));
  };

  const removeAttendee = (type, index) => {
    setRsvpData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const updateAttendee = (type, index, field, value) => {
    setRsvpData(prev => ({
      ...prev,
      [type]: prev[type].map((att, i) => 
        i === index ? { ...att, [field]: value } : att
      )
    }));
  };

  const handleRSVP = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;

    // Validate at least one attendee
    if (rsvpData.adults.length === 0 && rsvpData.children.length === 0) {
      toast.error('Please add at least one attendee');
      return;
    }

    // Validate all attendees have names
    const allAttendees = [...rsvpData.adults, ...rsvpData.children];
    if (allAttendees.some(a => !a.name.trim())) {
      toast.error('All attendees must have a name');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/events/${selectedEvent.id}/register`, rsvpData);
      toast.success('Successfully registered! You will receive a reminder before the event.');
      setRsvpOpen(false);
      setRsvpData({
        adults: [{ name: '', age: '', support_needs: '', allergies: '' }],
        children: []
      });
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register');
    } finally {
      setSubmitting(false);
    }
  };

  const openRSVP = (event) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to register for events');
      return;
    }
    setSelectedEvent(event);
    setRsvpOpen(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent({
      ...event,
      date: event.date?.split('T')[0] || ''
    });
    setEditOpen(true);
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/events/${editingEvent.id}`, editingEvent, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event updated successfully');
      setEditOpen(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted successfully');
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  // Camp handlers
  const handleEditCamp = (camp) => {
    setEditingCamp({ ...camp });
    setEditCampOpen(true);
  };

  const handleUpdateCamp = async () => {
    if (!editingCamp) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/camps/${editingCamp.id}`, editingCamp, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Camp updated successfully');
      setEditCampOpen(false);
      setEditingCamp(null);
      fetchCamps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update camp');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCamp = async (campId) => {
    if (!window.confirm('Are you sure you want to delete this camp?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/camps/${campId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Camp deleted successfully');
      fetchCamps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete camp');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 py-12">
        <div className="container-main">
          <div className="max-w-2xl mx-auto text-center">
            <div className="flex justify-center mb-4">
              <img src="/infinity.png" alt="Neurodiversity Infinity Symbol" className="h-16 w-auto" />
            </div>
            <Badge className="mb-4">Events & Camps</Badge>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Events & Inclusive Camps</h1>
            <p className="text-muted-foreground text-lg">
              Join our monthly webinars with Chair Chelsea, family-friendly events, 
              workshops, community meetups, and inclusive camps for all ages.
            </p>
          </div>
        </div>
      </div>

      <div className="container-main py-8">
        <Tabs defaultValue="events" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="events" className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              Events
            </TabsTrigger>
            <TabsTrigger value="camps" className="flex items-center gap-2">
              <Tent className="h-4 w-4" />
              Camps
            </TabsTrigger>
          </TabsList>

          {/* Events Tab */}
          <TabsContent value="events">
            {loading ? (
              <div className="grid md:grid-cols-2 gap-6">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-6">
                      <div className="h-6 bg-muted rounded w-1/2 mb-4"></div>
                      <div className="h-4 bg-muted rounded w-full mb-2"></div>
                      <div className="h-4 bg-muted rounded w-2/3"></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : events.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Upcoming Events</h3>
                  <p className="text-muted-foreground">
                    Check back soon for new events and webinars!
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {events.map((event) => (
                  <Card key={event.id} className="card-base" data-testid={`event-${event.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <Badge className={`${eventTypes[event.event_type]?.color || 'bg-primary'} text-white mb-2`}>
                            {eventTypes[event.event_type]?.label || event.event_type}
                          </Badge>
                          <CardTitle className="text-xl">{event.title}</CardTitle>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary">
                            {format(parseISO(event.date), 'd')}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {format(parseISO(event.date), 'MMM yyyy')}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-muted-foreground">{event.description}</p>
                      
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          {event.time}
                        </span>
                        {event.location && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <MapPin className="h-4 w-4" />
                            {event.location}
                          </span>
                        )}
                        {event.virtual_link && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Video className="h-4 w-4" />
                            Virtual
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Users className="h-4 w-4" />
                          {event.attendees_count} registered
                          {event.max_attendees && ` / ${event.max_attendees} max`}
                        </span>
                      </div>

                      <Button 
                        className="w-full btn-primary"
                        onClick={() => openRSVP(event)}
                        data-testid={`rsvp-${event.id}`}
                      >
                        <Check className="mr-2 h-4 w-4" />
                        RSVP Now
                      </Button>
                      
                      {/* Admin Edit/Delete Buttons */}
                      {isAdmin && (
                        <div className="flex gap-2 mt-3">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => handleEditEvent(event)}
                            data-testid={`edit-event-${event.id}`}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => handleDeleteEvent(event.id)}
                            data-testid={`delete-event-${event.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Camps Tab */}
          <TabsContent value="camps">
            <div className="space-y-6">
              {/* Camp Disclaimer Banner */}
              <Card className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-amber-100 dark:bg-amber-900 rounded-full">
                      <Sun className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Disclaimer</h3>
                      <p className="text-muted-foreground text-sm">
                        These camps have been described as "inclusive" by the providers. Autism Acceptance 
                        has not vetted the provider/event. It is the responsibility of the parent/caregiver 
                        or autistic individual to contact the provider directly to assess and share specific support needs.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Camps List */}
              <div className="grid md:grid-cols-2 gap-6">
                {camps.length === 0 ? (
                  <Card className="col-span-2 p-8 text-center">
                    <p className="text-muted-foreground">No camps scheduled at the moment. Check back soon!</p>
                  </Card>
                ) : camps.map((camp) => (
                  <Card key={camp.id} className="card-base" data-testid={`camp-${camp.id}`}>
                    {camp.image && (
                      <div className="h-40 overflow-hidden rounded-t-lg">
                        <img src={camp.image} alt={camp.name} className="w-full h-full object-cover object-top" />
                      </div>
                    )}
                    <CardHeader>
                      <Badge className="w-fit bg-teal-500 text-white mb-2">
                        <Tent className="h-3 w-3 mr-1" />
                        Inclusive Camp
                      </Badge>
                      <CardTitle className="text-xl">{camp.name}</CardTitle>
                      <CardDescription>{camp.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Dates:</span>
                          <p className="text-muted-foreground">{camp.dates}</p>
                        </div>
                        <div>
                          <span className="font-medium">Ages:</span>
                          <p className="text-muted-foreground">{camp.ages}</p>
                        </div>
                        <div>
                          <span className="font-medium">Location:</span>
                          <p className="text-muted-foreground">{camp.location}</p>
                        </div>
                        {camp.price && (
                          <div>
                            <span className="font-medium">Price:</span>
                            <p className="text-muted-foreground">{camp.price}</p>
                          </div>
                        )}
                      </div>

                      <a href={`mailto:${camp.contact_email || 'admin@autismacceptance.ky'}?subject=Camp Inquiry: ${camp.name}`}>
                        <Button className="w-full btn-primary">
                          <Check className="mr-2 h-4 w-4" />
                          Inquire About This Camp
                        </Button>
                      </a>
                      
                      {/* Admin Edit/Delete for Camps */}
                      {isAdmin && (
                        <div className="flex gap-2 mt-3">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => handleEditCamp(camp)}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => handleDeleteCamp(camp.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* More Info Card */}
              <Card className="border-dashed">
                <CardContent className="p-6 text-center">
                  <Tent className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">Looking for More Camp Options?</h3>
                  <p className="text-muted-foreground mb-4">
                    Check our Directory for a list of inclusive camps and summer programmes in the Cayman Islands.
                  </p>
                  <Link to="/directory">
                    <Button variant="outline">
                      View Directory
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* RSVP Dialog */}
        <Dialog open={rsvpOpen} onOpenChange={setRsvpOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Register for {selectedEvent?.title}</DialogTitle>
              <DialogDescription>
                Please provide details for all attendees. We collect this information 
                to ensure we can accommodate support needs and allergies.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleRSVP} className="space-y-6">
              {/* Adults */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Adults</h3>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => addAttendee('adults')}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Adult
                  </Button>
                </div>
                {rsvpData.adults.map((adult, index) => (
                  <Card key={index} className="p-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Name *</Label>
                        <Input
                          value={adult.name}
                          onChange={(e) => updateAttendee('adults', index, 'name', e.target.value)}
                          required
                          data-testid={`adult-name-${index}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Age</Label>
                        <Input
                          value={adult.age}
                          onChange={(e) => updateAttendee('adults', index, 'age', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label>Support Needs</Label>
                        <Input
                          placeholder="e.g., Noise-canceling headphones, quiet space..."
                          value={adult.support_needs}
                          onChange={(e) => updateAttendee('adults', index, 'support_needs', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label>Allergies</Label>
                        <Input
                          placeholder="e.g., Peanuts, gluten..."
                          value={adult.allergies}
                          onChange={(e) => updateAttendee('adults', index, 'allergies', e.target.value)}
                        />
                      </div>
                    </div>
                    {rsvpData.adults.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="mt-2 text-destructive"
                        onClick={() => removeAttendee('adults', index)}
                      >
                        Remove
                      </Button>
                    )}
                  </Card>
                ))}
              </div>

              {/* Children */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Children</h3>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => addAttendee('children')}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Child
                  </Button>
                </div>
                {rsvpData.children.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No children added. Click "Add Child" if you're bringing children.
                  </p>
                ) : (
                  rsvpData.children.map((child, index) => (
                    <Card key={index} className="p-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Name *</Label>
                          <Input
                            value={child.name}
                            onChange={(e) => updateAttendee('children', index, 'name', e.target.value)}
                            required
                            data-testid={`child-name-${index}`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Age *</Label>
                          <Input
                            value={child.age}
                            onChange={(e) => updateAttendee('children', index, 'age', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                          <Label>Support Needs</Label>
                          <Input
                            placeholder="e.g., Sensory breaks, visual schedule..."
                            value={child.support_needs}
                            onChange={(e) => updateAttendee('children', index, 'support_needs', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                          <Label>Allergies</Label>
                          <Input
                            placeholder="e.g., Dairy, eggs..."
                            value={child.allergies}
                            onChange={(e) => updateAttendee('children', index, 'allergies', e.target.value)}
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="mt-2 text-destructive"
                        onClick={() => removeAttendee('children', index)}
                      >
                        Remove
                      </Button>
                    </Card>
                  ))
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setRsvpOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="submit-rsvp">
                  {submitting ? 'Registering...' : 'Complete Registration'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Event Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Event</DialogTitle>
              <DialogDescription>Update the event details below.</DialogDescription>
            </DialogHeader>
            {editingEvent && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-title">Event Title</Label>
                  <Input
                    id="edit-title"
                    value={editingEvent.title}
                    onChange={(e) => setEditingEvent({...editingEvent, title: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editingEvent.description}
                    onChange={(e) => setEditingEvent({...editingEvent, description: e.target.value})}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-date">Date</Label>
                    <Input
                      id="edit-date"
                      type="date"
                      value={editingEvent.date}
                      onChange={(e) => setEditingEvent({...editingEvent, date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-time">Time</Label>
                    <Input
                      id="edit-time"
                      value={editingEvent.time}
                      onChange={(e) => setEditingEvent({...editingEvent, time: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-location">Location</Label>
                  <Input
                    id="edit-location"
                    value={editingEvent.location || ''}
                    onChange={(e) => setEditingEvent({...editingEvent, location: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-max">Max Attendees (optional)</Label>
                  <Input
                    id="edit-max"
                    type="number"
                    value={editingEvent.max_attendees || ''}
                    onChange={(e) => setEditingEvent({...editingEvent, max_attendees: e.target.value ? parseInt(e.target.value) : null})}
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                  <Button onClick={handleUpdateEvent} disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Camp Dialog */}
        <Dialog open={editCampOpen} onOpenChange={setEditCampOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Camp</DialogTitle>
              <DialogDescription>Update the camp details below.</DialogDescription>
            </DialogHeader>
            {editingCamp && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="camp-name">Camp Name</Label>
                  <Input
                    id="camp-name"
                    value={editingCamp.name}
                    onChange={(e) => setEditingCamp({...editingCamp, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="camp-description">Description</Label>
                  <Textarea
                    id="camp-description"
                    value={editingCamp.description}
                    onChange={(e) => setEditingCamp({...editingCamp, description: e.target.value})}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="camp-dates">Dates</Label>
                    <Input
                      id="camp-dates"
                      value={editingCamp.dates}
                      onChange={(e) => setEditingCamp({...editingCamp, dates: e.target.value})}
                      placeholder="July 14-18, 2026"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="camp-ages">Ages</Label>
                    <Input
                      id="camp-ages"
                      value={editingCamp.ages}
                      onChange={(e) => setEditingCamp({...editingCamp, ages: e.target.value})}
                      placeholder="6-12 years"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="camp-location">Location</Label>
                    <Input
                      id="camp-location"
                      value={editingCamp.location || ''}
                      onChange={(e) => setEditingCamp({...editingCamp, location: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="camp-price">Price</Label>
                    <Input
                      id="camp-price"
                      value={editingCamp.price || ''}
                      onChange={(e) => setEditingCamp({...editingCamp, price: e.target.value})}
                      placeholder="$450"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="camp-email">Contact Email</Label>
                  <Input
                    id="camp-email"
                    type="email"
                    value={editingCamp.contact_email || ''}
                    onChange={(e) => setEditingCamp({...editingCamp, contact_email: e.target.value})}
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditCampOpen(false)}>Cancel</Button>
                  <Button onClick={handleUpdateCamp} disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
